import React, { useEffect, useState } from 'react';
import { motion, useScroll, useTransform, useInView } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useInView as useInViewHook } from 'react-intersection-observer';
import { StatsSection } from '../components';
import SEOHead from '../components/SEOHead';

const HomePage = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  return (
    <div className="home-page">
      <SEOHead 
        title="AI Cinematic Video Production for Brands | Raw Visual Studio"
        description="Raw Visual Studio creates AI-powered cinematic brand films for Fashion, Beauty, Technology, Automotive, Events, Documentaries and beyond. Impossible shots. Real craft."
        canonical="https://raw-visualstudio.com/"
        ogType="website"
        schema={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "Raw Visual Studio",
          "url": "https://raw-visualstudio.com",
          "logo": "https://raw-visualstudio.com/favicon.png",
          "description": "AI-powered cinematic video production agency for brands and creators across Fashion, Beauty, SaaS, Technology, Automotive, Events, Film, Documentaries, and beyond.",
          "areaServed": ["US", "EU", "PL"],
          "sameAs": [
            "https://www.linkedin.com/company/raw-visualstudio/",
            "https://www.instagram.com/raw_visualstudio/",
            "https://www.facebook.com/profile.php?id=100089237994368",
            "https://www.tiktok.com/@raw_visualstudio"
          ],
          "serviceType": [
            "AI Video Production",
            "Commercial Film Production",
            "Fashion Film Production",
            "Automotive Commercial Production",
            "Documentary Production",
            "Event Film Production",
            "SaaS Product Video Production"
          ]
        }}
      />
      {/* Hero Section */}
      <HeroSection />

      {/* Stats Section */}
      <StatsSection />

      {/* Footer */}
      <Footer />
    </div>
  );
};

const HeroSection = () => {
  const navigate = useNavigate();
  const [ref, inView] = useInViewHook({
    threshold: 0.1,
    triggerOnce: true,
  });

  return (
    <section
      ref={ref}
      className="min-h-[100vh] md:min-h-screen flex items-center justify-center relative overflow-hidden pt-32 pb-20 md:pb-0"
    >
      {/* Background Video */}
      <div className="absolute inset-0 w-full h-full z-0" style={{ backgroundColor: '#0a0a0a' }}>
        <video
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          fetchPriority="high"
          className="w-full h-full object-cover"
        >
          <source
            src="https://pub-646a152084434420ba300869371a2687.r2.dev/rawvisualstudioldportf/VIDEOS/test%201%20.mp4"
            type="video/mp4"
          />
        </video>
        {/* Overlay to ensure text readability */}
        <div className="absolute inset-0 bg-black/60 md:bg-black/50" />
      </div>

      <div className="text-center px-4 max-w-7xl mx-auto mt-16 md:mt-16 w-full mb-10 md:mb-0 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.2, ease: "easeOut" }}
          className="relative mb-8 w-full"
        >
          <h1
            className="font-black text-gray-200/80 leading-none tracking-tight w-full"
            style={{ fontSize: "clamp(2.5rem, 10vw, 12rem)" }}
          >
            SUSTAINABLE
          </h1>
          <motion.h2
            initial={{ opacity: 0, y: 50 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 1.2, delay: 0.3, ease: "easeOut" }}
            className="font-black text-gray-200/80 leading-none tracking-tight md:-mt-6 mt-2 w-full"
            style={{ fontSize: "clamp(2.5rem, 10vw, 12rem)" }}
          >
            STORYTELLING
          </motion.h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 0.8 }}
          className="text-gray-300/70 text-lg md:text-xl mt-12 max-w-2xl mx-auto font-light tracking-wide"
        >
          We turn cameras into storytelling machines. Raw Visual Studio is a Warsaw-based creative studio blending cinematic filmmaking with AI-powered visuals — built in the underground, refined for the world stage. From electronic music festivals to luxury brands, from Warsaw to international screens.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 1.2 }}
          className="mt-16"
        >
          <button
            onClick={() => navigate('/gallery')}
            className="
              px-10 py-3
              rounded-full
              border border-white/30
              text-white
              text-sm
              font-semibold
              tracking-widest
              uppercase
              
              transition-all
              duration-300
              
              hover:border-white/80
              hover:shadow-[0_0_15px_rgba(255,255,255,0.15)]
              hover:opacity-[1.05]
              
              focus-visible:outline-none
              focus-visible:ring-2
              focus-visible:ring-white/50
            "
          >
            WITNESS THE CRAFT
          </button>
        </motion.div>
      </div>
    </section>
  );
};

// Removed Emmy and Services grids from homepage per new design

const Footer = () => {
  const [ref, inView] = useInViewHook({
    threshold: 0.3,
    triggerOnce: true,
  });

  return (
    <footer
      ref={ref}
      className="min-h-[70vh] md:min-h-screen bg-gradient-to-b from-[#000000] to-[#000000] flex items-center justify-center relative py-16 md:py-0 mt-10 md:mt-0"
    >
      <div className="text-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 100 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1.5, ease: "easeOut" }}
        >
          <h1 className="text-[5rem] md:text-[8rem] lg:text-[12rem] font-black text-gray-200/70 leading-none tracking-tight md:mb-8 mb-2">
            THE RAW
          </h1>
          <h2 className="text-[3.5rem] md:text-[6rem] lg:text-[8rem] font-black text-gray-300/50 leading-none tracking-tight md:-mt-8 mt-0">
            FRAMEWORK
          </h2>
          <h3 className="text-[2rem] md:text-[3rem] lg:text-[4rem] font-black text-gray-400/40 leading-none tracking-tight md:-mt-4 mt-0">
            ™
          </h3>
        </motion.div>

        <motion.p
          initial={{ opacity: 0 }}
          animate={inView ? { opacity: 1 } : {}}
          transition={{ duration: 1, delay: 0.5 }}
          className="text-gray-300/60 text-lg max-w-3xl mx-auto mt-12 font-light leading-relaxed"
        >
          We tear out everything generic. We build the visual world around the real story worth telling. Then we execute with full force — the kind of film that stops a scroll, holds a room, and stays in the mind long after it ends. Film is film. The craft doesn't change — only the tools do.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 1, delay: 0.8 }}
          className="mt-16 flex flex-col items-center space-y-4"
        >
          <div className="text-gray-400/40 text-sm tracking-[0.3em] font-light">
            18M+ VIEWS • 10+ YEARS EXPERIENCE • 24+ COUNTRIES • 150+ BRANDS
          </div>
          <div className="text-gray-500/30 text-xs tracking-widest font-light">
            © 2026 Raw Visual Studio • The Raw Framework™
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default HomePage;